import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { getPage } from "@/content/pages";
import { listPosts } from "@/content/blog";
import { getLocation, locationSlugs } from "@/content/locations";
import type { LocationTopic } from "@/content/types";
import { MarkdownBody } from "@/components/MarkdownBody";
import { PageHero } from "@/components/PageHero";
import { CtaBand } from "@/components/sections/CtaBand";
import { pageMetaTitle } from "@/lib/seo";

const PAGE_KEY = "/sitemap";
const LOCATION_TOPICS: Array<{ slug: LocationTopic; label: string }> = [
  { slug: "pricing", label: "Pricing" },
  { slug: "consultant-vs-agency", label: "Consultant vs agency" },
  { slug: "trades", label: "Trades SEO" },
  { slug: "professional-services", label: "Professional services SEO" },
  { slug: "ai-search", label: "AI search and GEO" },
];

export const Route = createFileRoute("/sitemap")({
  loader: () => {
    const page = getPage(PAGE_KEY);
    if (!page) throw notFound();
    return page;
  },
  head: ({ loaderData }) => ({
    meta: loaderData
      ? [
          { title: pageMetaTitle(PAGE_KEY, loaderData.title) },
          { name: "description", content: loaderData.description },
          { property: "og:title", content: loaderData.title },
          { property: "og:description", content: loaderData.description },
      { property: "og:url", content: "https://www.phil-carr.co.uk/sitemap" },
        ]
      : [],
    links: [{ rel: "canonical", href: "https://www.phil-carr.co.uk/sitemap" }],
  }),
  notFoundComponent: () => (
    <div className="container mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-display text-4xl">Page not found</h1>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="container mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-display text-3xl">Something went wrong</h1>
      <p className="mt-3 text-muted-foreground">{error.message}</p>
    </div>
  ),
  component: Page,
});

function Page() {
  const page = Route.useLoaderData();
  const pageTitle = page.body.match(/^#\s+(.+)$/m)?.[1]?.trim() ?? page.title;
  const bodyWithoutTitle = page.body.replace(/^#\s+.+\n+/, "");
  const posts = listPosts();
  return (
    <>
    <PageHero title={pageTitle} />
    <article className="inner-content-section container mx-auto max-w-5xl px-6 py-20 lg:px-10 lg:py-28">
      <MarkdownBody>{bodyWithoutTitle}</MarkdownBody>
      <section className="sitemap-link-hub" aria-labelledby="sitemap-local-title">
        <h2 id="sitemap-local-title">Local SEO page groups</h2>
        {locationSlugs.map((slug) => {
          const location = getLocation(slug);
          if (!location) return null;
          const label = slug.charAt(0).toUpperCase() + slug.slice(1);
          return (
            <div key={slug} className="sitemap-link-group">
              <h3>
                <Link to="/seo/$location" params={{ location: slug }}>
                  SEO {label}
                </Link>
              </h3>
              <ul>
                {LOCATION_TOPICS.map((topic) =>
                  location.topics[topic.slug] ? (
                    <li key={topic.slug}>
                      <Link
                        to="/seo/$location/$topic"
                        params={{ location: slug, topic: topic.slug }}
                      >
                        {label} {topic.label}
                      </Link>
                    </li>
                  ) : null,
                )}
              </ul>
            </div>
          );
        })}
      </section>
      <section className="sitemap-link-hub" aria-labelledby="sitemap-blog-title">
        <h2 id="sitemap-blog-title">Full news archive</h2>
        <ul className="sitemap-archive-list">
          {posts.map((post) => (
            <li key={post.slug}>
              <Link to="/blog/$slug" params={{ slug: post.slug }}>
                {post.title.replace(/\s*\|\s*Phil Carr SEO Consultant\s*$/i, "").trim()}
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </article>
    <CtaBand />
    </>
  );
}
